import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import React from 'react'
import { toast } from 'react-toastify'

import {
  deleteIngredient,
  duplicateIngredient,
  foodGroupsQuery,
  foodRestrictionsQuery,
  ingredientQuery,
  saveIngredient,
  toggleIngredientVisibility,
} from '@/api'
import { Ingredient } from '@/api/types'
import EditIngredient from '@/features/ingredients/EditIngredient'
import { languagesQuery } from '@/api/languages'

export const Route = createFileRoute('/ingredients_/fcdb_/$ingId')({
  loader: async ({ context: { queryClient }, params }) => {
    const [ingredient, foodGroups, foodRestrictions, languages] = await Promise.all([
      queryClient.ensureQueryData(ingredientQuery(params.ingId)),
      queryClient.ensureQueryData(foodGroupsQuery),
      queryClient.ensureQueryData(foodRestrictionsQuery),
      queryClient.ensureQueryData(languagesQuery),
    ])

    return { foodGroups, foodRestrictions, ingredient, languages }
  },
  component: IngredientComponent,
})

function IngredientComponent() {
  const { foodGroups, foodRestrictions, ingredient, languages } = Route.useLoaderData()
  const queryClient = useQueryClient()
  const router = useRouter()

  const saveMutation = useMutation({
    mutationFn: (formData: Ingredient) => saveIngredient(formData),
    onSuccess: (res) => {
      if (res.status === 204) {
        queryClient.removeQueries({
          queryKey: ingredientQuery(ingredient.idIngredient + '').queryKey,
        })
        toast.success('Ingredient saved with success')
      } else {
        toast.error('There was an error saving')
      }
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: () => duplicateIngredient(ingredient.idIngredient),
    onSuccess: (res) => {
      if (res.status === 200) {
        queryClient.removeQueries({
          queryKey: ['ingredients'],
        })

        toast.success('Ingredient duplicated with success')

        router.navigate({
          to: '/ingredients/fcdb/$ingId',
          params: {
            ingId: res.data.idIngredient!.toString(),
          },
        })
      } else {
        toast.error('There was an error duplicating')
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteIngredient(ingredient.idIngredient),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['ingredient', ingredient.idIngredient],
      })

      queryClient.setQueriesData<Ingredient[]>({ queryKey: ['ingredients'] }, (oldData) => {
        if (oldData) {
          return oldData.filter((item) => item.idIngredient !== ingredient.idIngredient)
        }
      })

      toast.success('Ingredient deleted with success')

      router.navigate({
        to: '/ingredients',
        hash: 'Fcdb',
      })
    },
  })

  const toggleVisibilityMutation = useMutation({
    mutationFn: (state: boolean) => toggleIngredientVisibility(ingredient.idIngredient, state),
  })

  const handleDelete = () => {
    if (confirm('Are sure you want to delete the ingredient?')) {
      deleteMutation.mutate()
    }
  }

  return (
    <EditIngredient
      ingredient={ingredient}
      foodGroups={foodGroups}
      foodRestrictions={foodRestrictions}
      languages={languages}
      saveIngredient={saveMutation}
      duplicateIngredient={duplicateMutation.mutateAsync}
      deleteIngredient={handleDelete}
      toggleVisibility={toggleVisibilityMutation}
    />
  )
}
